import { ElMessage } from 'element-plus';

import { fetchActivityEntry } from '@/common/hooks/useActivityEntry';
import { LOCAL_STORAGE_KEY } from '@/constants';
import { ACTIVITY_NBI_UPDATED_EVENT } from '@/constants/activityNbi';
import { reportNewbieTaskComplete } from '@/jmHttp/api/activityNbi';
import { emitter } from '@/plugins/emitter';
import { storage } from '@/utils/storageOperateApi';

/**
 * @file 新手任务完成上报与待庆祝队列
 * @module utils/newbieTaskReport
 * 供各业务功能触发点（查看纪要、查看内参、查看外资研报、浏览自选、AI 进宝三种模式）调用，
 * 接口本身幂等，此处再做一层会话级去重，避免同一功能反复请求。
 * 碎纸动效需落在对应任务行上，故先记入待庆祝队列：浮窗开着时由刷新流程立即消费，收起时留到下次展示再补播。
 */

/** 本次会话内已上报成功的 bizType */
const reportedBizTypes = new Set();

/** 待庆祝队列的保留天数：超期未打开过任务卡片则不再补播，避免隔很久突然撒花 */
const PENDING_CELEBRATE_EXPIRE_DAYS = 7;

/**
 * 读取待庆祝的 bizType 列表。
 * @returns {number[]} 待庆祝的 bizType，无数据时为空数组
 */
function readPendingCelebrate() {
  const stored = storage.get(LOCAL_STORAGE_KEY.NEWBIE_TASK_PENDING_CELEBRATE);
  return Array.isArray(stored) ? stored.filter((item) => Number(item) > 0).map(Number) : [];
}

/**
 * 将某个任务记入待庆祝队列，等任务卡片打开时播放碎纸动效。
 * 用 localStorage 而非内存变量，是为了让刷新页面、或在另一个标签页打开卡片时仍能补播。
 * @param {number} bizType - 业务功能类型，取值见 NEWBIE_TASK_BIZ_TYPE
 */
function markPendingCelebrate(bizType) {
  const pending = readPendingCelebrate();
  if (pending.includes(bizType)) return;
  storage.set(LOCAL_STORAGE_KEY.NEWBIE_TASK_PENDING_CELEBRATE, [...pending, bizType], PENDING_CELEBRATE_EXPIRE_DAYS);
}

/**
 * 取出并清空待庆祝的 bizType 列表，由新手任务卡片在渲染完成后调用。
 * 一次性消费：取出即清除，保证同一次完成只庆祝一次。
 * @returns {number[]} 需要播放碎纸动效的 bizType
 * @example
 * const bizTypes = consumePendingCelebrate();
 * bizTypes.forEach((bizType) => fireCelebrateConfetti(rowRefs.get(bizType)));
 */
export function consumePendingCelebrate() {
  const pending = readPendingCelebrate();
  if (pending.length) {
    storage.remove(LOCAL_STORAGE_KEY.NEWBIE_TASK_PENDING_CELEBRATE);
  }
  return pending;
}

/**
 * 上报某个新手任务已完成，首次完成时提示文案并记入待庆祝队列。
 * 内部已吞掉所有异常，调用方无需 try/catch，也不应 await 阻塞业务主流程。
 * @param {number} bizType - 业务功能类型，取值见 NEWBIE_TASK_BIZ_TYPE
 * @returns {Promise<void>}
 * @example
 * reportNewbieTask(NEWBIE_TASK_BIZ_TYPE.VIEW_SUMMARY);
 */
export async function reportNewbieTask(bizType) {
  if (!bizType || reportedBizTypes.has(bizType)) return;

  // 无新手任务入口（活动未开启 / 用户无资格）时无需上报
  const entry = await fetchActivityEntry();
  if (!entry.newbieEntry) return;

  // 先占位再请求，避免同一功能被连续触发时并发上报
  reportedBizTypes.add(bizType);

  try {
    const res = await reportNewbieTaskComplete({ bizType }, { hideMessage: true });
    const data = res?.data || {};

    // 未完成（如不满足条件）时释放占位，允许下次触发重试
    if (!data.completed) {
      reportedBizTypes.delete(bizType);
      return;
    }

    // 此前已完成属于幂等重复上报，不再打扰用户
    if (data.alreadyCompleted) return;

    if (data.message) {
      ElMessage.success(data.message);
    }
    markPendingCelebrate(bizType);
    emitter.emit(ACTIVITY_NBI_UPDATED_EVENT);
  } catch (err) {
    reportedBizTypes.delete(bizType);
    console.error('新手任务完成上报失败：', err);
  }
}
